import { scheduleDateSchema } from "@learn-flow/contracts";

import { parseActivityReturnContext } from "../../lib/activity-return-context.js";

export type CalendarActivityCreationIntent = Readonly<{
  scheduledDate: string;
  returnTo: string;
}>;

const editorPath = "/activities/new";
const relativeUrlBase = "https://learnflow.invalid";

export function createCalendarActivityEditorUrl(intent: CalendarActivityCreationIntent): string {
  const parsed = parseCalendarActivityCreationIntent(
    new URLSearchParams({
      scheduleDate: intent.scheduledDate,
      returnTo: intent.returnTo,
    }),
  );
  if (!parsed) throw new Error("A valid Calendar activity creation intent is required.");
  const parameters = new URLSearchParams({
    scheduleDate: parsed.scheduledDate,
    returnTo: parsed.returnTo,
  });
  return `${editorPath}?${parameters.toString()}`;
}

export function parseCalendarActivityCreationIntent(
  parameters: URLSearchParams,
): CalendarActivityCreationIntent | null {
  if (
    parameters.getAll("scheduleDate").length !== 1 ||
    parameters.getAll("returnTo").length !== 1
  ) {
    return null;
  }
  const date = scheduleDateSchema.safeParse(parameters.get("scheduleDate"));
  const returnTo = parameters.get("returnTo");
  const returnContext = parseActivityReturnContext(returnTo);
  if (!date.success || !returnContext || returnContext.label !== "Calendar") return null;

  let returnUrl: URL;
  try {
    returnUrl = new URL(returnContext.destination, relativeUrlBase);
  } catch {
    return null;
  }
  const returnParameters = returnUrl.searchParams;
  const returnKeys = [...returnParameters.keys()];
  if (
    returnKeys.some((key) => key !== "start" && key !== "selected") ||
    returnParameters.getAll("start").length !== 1 ||
    !scheduleDateSchema.safeParse(returnParameters.get("start")).success
  ) {
    return null;
  }
  const selectedValues = returnParameters.getAll("selected");
  if (
    selectedValues.length > 1 ||
    (selectedValues.length === 1 && !scheduleDateSchema.safeParse(selectedValues[0]).success)
  ) {
    return null;
  }
  return {
    scheduledDate: date.data,
    returnTo: returnContext.destination,
  };
}
